import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { Alert, Box, Button, CircularProgress, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { addYears, format } from 'date-fns';
import React from 'react';
import { usePricing } from '../../../hooks/usePricing';
import { useTranslation } from '../../../hooks/useTranslation';
import { useUserPlanStatus } from '../../../hooks/useUserPlanStatus'; // Import the plan status hook
import type { AddonApiKey, Addons, FirestoreTimestamp, Package } from '../../../services/payment.types';
import { convertCentsToEuros, formatPrice } from '../../../services/transformers';
import { calculateEffectiveDisplayPriceEuros, convertTimestampToDate } from '../utils'; // Import the utilities

const CouponAlert: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    // Dark mode uses a darker background with bright text
    // Light mode uses a light blue background with dark blue text
    const styles = {
        backgroundColor: isDarkMode ? '#1e3a5f' : '#e3f2fd',
        textPrimary: isDarkMode ? '#ffffff' : '#1565c0',
        textSecondary: isDarkMode ? '#bbdefb' : '#1976d2',
        border: isDarkMode ? '#2196f3' : '#bbdefb',
        buttonBg: '#2196f3',
        buttonHover: '#1976d2',
        iconColor: isDarkMode ? '#90caf9' : '#2196f3',
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Alert
                severity="info"
                icon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: '1.2rem', color: styles.iconColor }} />}
                sx={{
                    backgroundColor: styles.backgroundColor,
                    color: styles.textPrimary,
                    fontWeight: 'medium',
                    fontSize: '0.9rem',
                    border: `1px solid ${styles.border}`,
                    borderRadius: 2,
                    p: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
                action={null}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        alignItems: 'flex-start',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 'medium',
                            fontSize: '1rem',
                            mb: 0,
                            color: styles.textPrimary,
                            lineHeight: 1.3,
                            wordBreak: 'break-word',
                        }}
                    >
                        {t('plans.orderSummary.couponApplied')}
                    </Typography>
                </Box>
            </Alert>
        </Box>
    );
};

interface OrderSummaryProps {
    hasSelections: boolean;
    selectedTaxYears: Record<
        string,
        {
            package: Package | null;
            expertReview: boolean;
        }
    >;
    additionalTransactions: Record<string, number>;
    addons: Addons;
    loading: boolean;
    error: string | null;
    onCheckout: () => void;
    isAddonPurchased?: (addonName: string, addonKey?: AddonApiKey | null) => boolean;
    getAddonValidUntil?: (addonName: string, addonKey?: AddonApiKey | null) => string | FirestoreTimestamp | undefined;
    celloDiscount: any | null;
    celloDiscountLoading: boolean;
    weDoEverythingCustomPricing?: {
        price: number;
        currency: string;
    } | null;
    planPageStep?: string;
}

export interface WeDoEverything {
    price: number;
    transactions: number;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
    hasSelections,
    selectedTaxYears,
    additionalTransactions,
    addons,
    loading,
    error,
    onCheckout,
    isAddonPurchased,
    getAddonValidUntil,
    celloDiscount,
    celloDiscountLoading,
    weDoEverythingCustomPricing,
    planPageStep,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    // Get pricing data from API
    const { pricing, isLoading: pricingLoading } = usePricing();
    // Get user plans data for upgrade calculation
    const { userPlans } = useUserPlanStatus();

    // Function to calculate subscription period based on whether it's a renewal
    const getSubscriptionPeriod = (addonName: string) => {
        // Check if the addon is already purchased
        const isPurchased = isAddonPurchased ? isAddonPurchased(addonName) : false;

        if (isPurchased && getAddonValidUntil) {
            // Get the current valid until date
            const validUntil = getAddonValidUntil(addonName);

            if (validUntil) {
                // Convert timestamp to Date using our utility function
                const date = convertTimestampToDate(validUntil);

                if (!date) {
                    // If date is invalid, use current date + 1 year
                    const now = new Date();
                    now.setFullYear(now.getFullYear() + 1);
                    return `${format(now, 'dd.MM.yyyy')}`;
                }

                // Calculate the new valid until date (1 year from current expiration)
                date.setFullYear(date.getFullYear() + 1);
                return `${format(date, 'dd.MM.yyyy')}`;
            }
        }

        // If not purchased or no valid until date, calculate from today
        const today = new Date();
        const nextYear = addYears(today, 1);
        return `${format(nextYear, 'dd.MM.yyyy')}`;
    };

    const calculateTotal = () => {
        if (!pricing) return '0.00';

        let total = 0;

        // Add tax year costs
        Object.entries(selectedTaxYears).forEach(([year, selection]) => {
            if (selection.package) {
                // Calculate effective price for this package
                const effectivePrice = calculateEffectiveDisplayPriceEuros(selection.package, year, userPlans, pricing);
                total += effectivePrice;
            }

            // Add expert review cost if selected
            if (selection.expertReview && pricing.addons.expertReview) {
                total += convertCentsToEuros(pricing.addons.expertReview.price);
            }

            // Add additional transactions cost
            if (additionalTransactions[year] && additionalTransactions[year] > 0) {
                total += additionalTransactions[year] * convertCentsToEuros(pricing.extraTransactions.price);
            }
        });

        // Add addon costs
        if (addons.portfolioInsights) total += convertCentsToEuros(pricing.addons.portfolioInsights.price);
        if (addons.taxOptimization) total += convertCentsToEuros(pricing.addons.taxOptimization.price);
        if (addons.weDoEverything) {
            // Use custom pricing if available, otherwise use regular pricing
            if (weDoEverythingCustomPricing?.price) {
                total += convertCentsToEuros(weDoEverythingCustomPricing.price);
            } else {
                total += convertCentsToEuros(pricing.addons.weDoEverything.price);
            }
        }

        return total.toFixed(2);
    };

    // Calculate the discounted total if a discount is available
    const calculateDiscountedTotal = () => {
        if (!pricing || !celloDiscount) return calculateTotal();

        const total = parseFloat(calculateTotal());

        // Apply discount based on validation data
        if (celloDiscount.validationResult?.discount?.percent_off) {
            // Percentage discount
            const discountAmount = total * (celloDiscount.validationResult.discount.percent_off / 100);
            return (total - discountAmount).toFixed(2);
        } else if (
            celloDiscount.validationResult?.discount?.amount_off &&
            (celloDiscount.validationResult.discount.currency === 'EUR' ||
                celloDiscount.validationResult.discount.currency === '€')
        ) {
            // Fixed amount discount (convert from cents to euros)
            const amountOff = celloDiscount.validationResult.discount.amount_off / 100;
            return Math.max(0, total - amountOff).toFixed(2);
        }

        return total.toFixed(2);
    };

    // Calculate the discount amount
    const calculateDiscountAmount = () => {
        if (!pricing || !celloDiscount) return '0.00';

        const total = parseFloat(calculateTotal());

        // Calculate discount amount based on validation data
        if (celloDiscount.validationResult?.discount?.percent_off) {
            // Percentage discount
            return (total * (celloDiscount.validationResult.discount.percent_off / 100)).toFixed(2);
        } else if (
            celloDiscount.validationResult?.discount?.amount_off &&
            (celloDiscount.validationResult.discount.currency === 'EUR' ||
                celloDiscount.validationResult.discount.currency === '€')
        ) {
            // Fixed amount discount (convert from cents to euros)
            const amountOff = celloDiscount.validationResult.discount.amount_off / 100;
            return Math.min(total, amountOff).toFixed(2);
        }

        return '0.00';
    };

    // Format the discount for display
    const getFormattedDiscount = () => {
        if (!celloDiscount) return '';

        if (celloDiscount.validationResult?.discount?.percent_off) {
            return `${celloDiscount.validationResult.discount.percent_off}%`;
        } else if (celloDiscount.validationResult?.discount?.amount_off) {
            const currency = celloDiscount.validationResult.discount.currency || 'EUR';
            const symbol = currency === 'EUR' ? '€' : currency;
            return `${symbol}${(celloDiscount.validationResult.discount.amount_off / 100).toFixed(2)}`;
        }

        return '';
    };

    // Generate the discount label (e.g., "10% COUPON")
    const getDiscountLabel = (): string => {
        if (!celloDiscount?.validationResult?.discount) return '';

        const { percent_off, amount_off, currency } = celloDiscount.validationResult.discount;

        if (percent_off) {
            return `${percent_off}% COUPON`;
        } else if (amount_off && currency) {
            const amount = (amount_off / 100).toFixed(0); // Assuming cents, show whole euros
            return `${amount} ${currency.toUpperCase()} COUPON`;
        }

        return ''; // Fallback if no valid discount data
    };

    const mainBox = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        p: { xs: 3, sm: 4 },
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        backgroundColor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        width: '100%',
        position: { xs: 'static', md: 'sticky' },
        mb: { xs: 2, md: 0 },
    };

    // Show loading state while pricing data or discount data is loading
    if (pricingLoading) {
        return (
            <Box sx={mainBox}>
                <Stack spacing={2} sx={{ width: '100%' }}>
                    {/* Title Skeleton */}
                    <Skeleton variant="text" width="60%" height={30} />
                    <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
                    {/* Item Skeletons (example for two items) */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Skeleton variant="text" width="70%" height={25} />
                        <Skeleton variant="text" width="20%" height={25} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Skeleton variant="text" width="60%" height={25} />
                        <Skeleton variant="text" width="25%" height={25} />
                    </Box>
                    {/* Addon Skeletons (optional, example for one) */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Skeleton variant="text" width="50%" height={20} />
                        <Skeleton variant="text" width="15%" height={20} />
                    </Box>
                    <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
                    {/* Total Skeleton */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Skeleton variant="text" width="30%" height={30} />
                        <Skeleton variant="text" width="40%" height={35} />
                    </Box>
                    {/* Button Skeleton */}
                    <Skeleton variant="rectangular" width="100%" height={48} sx={{ mt: 1, borderRadius: 2 }} />
                    {/* Footer Text Skeletons */}
                    <Skeleton variant="text" width="70%" height={20} />
                    <Skeleton variant="text" width="50%" height={20} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box sx={mainBox}>
            {!celloDiscountLoading && !!celloDiscount && <CouponAlert />}
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, fontSize: '1.25rem' }}>
                {t('plans.orderSummary.title')}
            </Typography>
            {!hasSelections ? (
                <Stack
                    spacing={2}
                    alignItems="center"
                    sx={{
                        flex: 1,
                        justifyContent: 'center',
                        minHeight: { xs: 150, sm: 200 },
                    }}
                >
                    <Typography variant="body2" color="text.secondary" align="center">
                        {planPageStep === 'expertChoice'
                            ? t('plans.orderSummary.emptyStateExpert')
                            : t('plans.orderSummary.emptyState')}
                    </Typography>
                </Stack>
            ) : (
                <Stack spacing={0} sx={{ width: '100%' }}>
                    {/* Selected Tax Years Summary */}
                    {Object.entries(selectedTaxYears).map(([year, yearData], index) => (
                        <Box key={year} sx={{ width: '100%' }}>
                            {index > 0 && <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />}
                            <Box sx={{ py: 0 }}>
                                {yearData.package && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 2,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography
                                                variant="body1"
                                                sx={{ fontSize: '1rem', color: theme.palette.text.primary }}
                                            >
                                                {t(`plansData.packages.${yearData.package.id}.short_description`, {
                                                    year,
                                                    transactions: yearData.package.transactions,
                                                })}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                                            {pricing && yearData.package
                                                ? formatPrice(
                                                      calculateEffectiveDisplayPriceEuros(
                                                          yearData.package,
                                                          year,
                                                          userPlans,
                                                          pricing
                                                      ),
                                                      pricing.currencySymbol
                                                  )
                                                : ''}
                                        </Typography>
                                    </Box>
                                )}
                                {yearData.expertReview && (
                                    <>
                                        <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                py: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{ fontSize: '1rem', color: theme.palette.text.primary }}
                                            >
                                                {t('plansData.addons.expertReview.short_description', { year })}
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                                                {pricing
                                                    ? formatPrice(
                                                          convertCentsToEuros(pricing.addons.expertReview.price),
                                                          pricing.currencySymbol
                                                      )
                                                    : '€29.99'}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>
                    ))}

                    {/* Addons Summary */}
                    {(addons.portfolioInsights ||
                        addons.taxOptimization ||
                        addons.weDoEverything ||
                        addons.individualSetup) && (
                        <Box sx={{ width: '100%' }}>
                            <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />

                            {addons.weDoEverything && (
                                <Box sx={{ py: 0 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 2,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: '1rem', color: theme.palette.text.primary }}
                                            >
                                                {t('plansData.addons.weDoEverything.short_description')}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                                            {weDoEverythingCustomPricing?.price
                                                ? formatPrice(
                                                      convertCentsToEuros(weDoEverythingCustomPricing.price),
                                                      weDoEverythingCustomPricing.currency === 'EUR'
                                                          ? '€'
                                                          : weDoEverythingCustomPricing.currency
                                                  )
                                                : pricing
                                                  ? formatPrice(
                                                        convertCentsToEuros(pricing.addons.weDoEverything.price),
                                                        pricing.currencySymbol
                                                    )
                                                  : '€299.99'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {addons.individualSetup && (
                                <Box sx={{ py: 0 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 2,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: '1rem', color: theme.palette.text.primary }}
                                            >
                                                {t('plans.expertOptions.customSolutionOrderSummary')}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                                            {t('plans.expertOptions.individualPriceOrderSummary')}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {addons.portfolioInsights && (
                                <>
                                    <Box sx={{ py: 0 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                py: 2,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontSize: '1rem', color: theme.palette.text.primary }}
                                                >
                                                    {t('plansData.addons.portfolioInsights.short_description', {
                                                        date: getSubscriptionPeriod('Portfolio Insights'),
                                                    })}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                                                {pricing
                                                    ? formatPrice(
                                                          convertCentsToEuros(pricing.addons.portfolioInsights.price),
                                                          pricing.currencySymbol
                                                      )
                                                    : '€9.99'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {addons.taxOptimization && (
                                <>
                                    {addons.portfolioInsights && (
                                        <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
                                    )}
                                    <Box sx={{ py: 0 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                py: 2,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontSize: '1rem', color: theme.palette.text.primary }}
                                                >
                                                    {t('plansData.addons.taxOptimization.short_description', {
                                                        date: getSubscriptionPeriod('Tax Optimization'),
                                                    })}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                                                {pricing
                                                    ? formatPrice(
                                                          convertCentsToEuros(pricing.addons.taxOptimization.price),
                                                          pricing.currencySymbol
                                                      )
                                                    : '€14.99'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}

                    {!addons.individualSetup && (
                        <>
                            <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
                            {/* Total */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                    pt: 1,
                                }}
                            >
                                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                                    {t('plans.orderSummary.total', 'Summe')}:
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                                    {pricing
                                        ? formatPrice(parseFloat(calculateTotal()), pricing.currencySymbol)
                                        : formatPrice(parseFloat(calculateTotal()))}
                                </Typography>
                            </Box>
                        </>
                    )}

                    {error && (
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={onCheckout}
                        disabled={loading}
                        sx={{
                            py: { xs: 1.5, sm: 1.5 },
                            mt: 4,
                            borderRadius: 2,
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 8px 25px rgba(25, 118, 210, 0.35)',
                            },
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={24} />
                        ) : addons.individualSetup ? (
                            t('plans.orderSummary.requestNow')
                        ) : (
                            t('plans.orderSummary.checkout')
                        )}
                    </Button>
                </Stack>
            )}
        </Box>
    );
};
